import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { User } from '../types';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import UserStatsService from '../services/UserStatsService';
// import PointService from '../services/PointService'; // For centralized point system

interface DailyPracticeGoalCardProps {
  currentUser: User | null;
  badgesLoaded: boolean;
  earnedBadges: string[];
  isPracticeSessionActive: boolean;
  currentSessionProgress: number;
  showResetButton?: boolean;
  onResetTimers?: () => void;
  // For teacher mode
  viewingStudent?: any; // Can be a partial User object or null
  isTeacherMode?: boolean;
  // For practice tasks mode
  plannedTotalMinutes?: number;
  overallRemainingTime?: number;
  formatTime?: (seconds: number) => string;
}

const DailyPracticeGoalCard: React.FC<DailyPracticeGoalCardProps> = ({
  currentUser,
  badgesLoaded,
  earnedBadges,
  isPracticeSessionActive,
  currentSessionProgress,
  showResetButton = false,
  onResetTimers,
  viewingStudent,
  isTeacherMode = false,
  plannedTotalMinutes = 0,
  overallRemainingTime = 0,
  formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [showStreakTooltip, setShowStreakTooltip] = useState(false);
  const [showPointsTooltip, setShowPointsTooltip] = useState(false);
  const [pointsTooltipPosition, setPointsTooltipPosition] = useState({ x: 0, y: 0 });
  const [streakTooltipPosition, setStreakTooltipPosition] = useState({ x: 0, y: 0 });
  const [badgeTooltipPosition, setBadgeTooltipPosition] = useState({ x: 0, y: 0 });

  // Internal weekly progress state
  const [weeklyCompletedSessions, setWeeklyCompletedSessions] = useState<any[]>([]);
  const [allWeeklySessions, setAllWeeklySessions] = useState<any[]>([]);
  const [allSessions, setAllSessions] = useState<any[]>([]); // For streak calculation
  const [weeklyProgressLoaded, setWeeklyProgressLoaded] = useState(false);
  
  // Streak state from centralized service
  const [currentStreak, setCurrentStreak] = useState<number>(0);
  const [streakLoading, setStreakLoading] = useState<boolean>(true);

  // Badge definitions for tooltip display
  const badgeDefinitions: Record<string, { emoji: string; name: string; description: string }> = {
    'first-day': { emoji: '🎯', name: 'First Steps', description: 'Completed your first practice session' },
    'three-day-streak': { emoji: '🔥', name: 'Hot Streak', description: '3-day practice streak' },
    'week-streak': { emoji: '⭐', name: 'Week Warrior', description: '7-day practice streak' },
    'two-week-streak': { emoji: '💪', name: 'Consistency King', description: '14-day practice streak' },
    'month-streak': { emoji: '👑', name: 'Practice Legend', description: '30-day practice streak' },
    'first-week': { emoji: '📅', name: 'Week Complete', description: 'Met weekly goal once' },
    'month-consistency': { emoji: '🏆', name: 'Monthly Master', description: '4 weeks of meeting goals' },
    'quarter-master': { emoji: '🎖️', name: 'Quarter Champion', description: '12 weeks of meeting goals' },
    'hour-master': { emoji: '⏰', name: 'Hour Hero', description: '1 hour total practice time' },
    'five-hour-club': { emoji: '🎵', name: 'Five Hour Club', description: '5 hours total practice time' },
    'thousand-minute-master': { emoji: '💯', name: 'Thousand Minute Master', description: '1000 minutes total practice' },
    'practice-legend': { emoji: '🌟', name: 'Practice Legend', description: '5000 minutes total practice' },
    'focused-practicer': { emoji: '🎯', name: 'Focused Practicer', description: 'Average 30+ min sessions' },
    'dedicated-musician': { emoji: '🎼', name: 'Dedicated Musician', description: 'Average 60+ min sessions' },
    'quick-learner': { emoji: '🚀', name: 'Quick Learner', description: 'Complete 7 practice sessions in your first week' },
    'curious-mind': { emoji: '🧠', name: 'Curious Mind', description: 'Try 5 different practice categories in one session' },
    'explorer': { emoji: '🗺️', name: 'Explorer', description: 'Practice 10 different pieces/exercises' },
    'technique-master': { emoji: '⚡', name: 'Technique Master', description: 'Focus on technique for 3 consecutive sessions' },
    'early-bird': { emoji: '🌅', name: 'Early Bird', description: 'Practice before 8 AM' },
    'night-owl': { emoji: '🦉', name: 'Night Owl', description: 'Practice after 8 PM' },
    'weekend-warrior': { emoji: '⚔️', name: 'Weekend Warrior', description: 'Complete practice on both weekend days' },
    'marathon-session': { emoji: '🏃', name: 'Marathon Session', description: 'Practice for 2+ hours in one session' },
    'first-month': { emoji: '📆', name: 'First Month', description: 'Complete 30 days of practice' },
    'half-year-hero': { emoji: '🎖️', name: 'Half Year Hero', description: 'Practice for 6 months' },
    'year-of-music': { emoji: '🎊', name: 'Year of Music', description: 'Practice for a full year' },
    'century-club': { emoji: '💯', name: 'Century Club', description: '100 total practice sessions' },
    'thousand-club': { emoji: '🎯', name: 'Thousand Club', description: '1000 total practice sessions' },
    'comeback-kid': { emoji: '🔄', name: 'Comeback Kid', description: 'Return after a 7+ day break' },
    'performance-ready': { emoji: '🎭', name: 'Performance Ready', description: 'Teacher says you\'re ready to perform!' },
    'breakthrough-moment': { emoji: '💡', name: 'Breakthrough Moment', description: 'Teacher recognised you had a major breakthrough' },
    'technique-perfectionist': { emoji: '✨', name: 'Technique Perfectionist', description: 'Teacher praised your technique work' },
    'musical-expression': { emoji: '🎨', name: 'Musical Expression', description: 'Teacher loved your musical interpretation' },
    'sight-reading-star': { emoji: '👁️', name: 'Sight Reading Star', description: 'Teacher impressed by your sight reading' },
    'memory-master': { emoji: '🧠', name: 'Memory Master', description: 'Teacher praised your memorisation skills' },
    'rhythm-king': { emoji: '🥁', name: 'Rhythm King/Queen', description: 'Teacher loved your rhythm work' },
    'practice-champion': { emoji: '🏅', name: 'Practice Champion', description: 'Teacher noticed your excellent practice habits' },
    'creative-genius': { emoji: '🎪', name: 'Creative Genius', description: 'Teacher praised your creativity' },
    'dedication-award': { emoji: '💎', name: 'Dedication Award', description: 'Teacher recognised your dedication' },
    'improvement-star': { emoji: '⭐', name: 'Improvement Star', description: 'Teacher noticed significant improvement' },
    'lesson-hero': { emoji: '🦸', name: 'Lesson Hero', description: 'Teacher said you had an amazing lesson' },
    'birthday-celebration': { emoji: '🎂', name: 'Birthday Practice Star', description: 'You practised on your birthday! What dedication!' }
  };

  // Centralized weekly progress loading function
  const loadWeeklyProgress = async () => {
    const targetUser = isTeacherMode ? viewingStudent : currentUser;
    if (!targetUser?.uid) return;
    
    try {
      // Calculate the start of the current week (Sunday)
      const now = new Date();
      const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - currentDay); // Go back to Sunday
      startOfWeek.setHours(0, 0, 0, 0); // Start of day
      
      // Calculate end of week (Saturday)
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999); // End of day
      
      const sessionsQuery = query(
        collection(db, 'practiceSessions'),
        where('studentId', '==', targetUser.uid),
        where('completed', '==', true),
        where('endAt', '>=', startOfWeek),
        where('endAt', '<=', endOfWeek),
        orderBy('endAt', 'desc')
      );
      
      const querySnapshot = await getDocs(sessionsQuery);
      const sessions: any[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        sessions.push({
          id: doc.id,
          studentId: data.studentId,
          lessonId: data.lessonId,
          plannedTotalMinutes: data.plannedTotalMinutes,
          plannedDurations: data.plannedDurations,
          actualDurations: data.actualDurations,
          completed: data.completed,
          startAt: data.startAt,
          endAt: data.endAt,
          durationSec: data.durationSec,
          taskCounts: data.taskCounts,
          createdAt: data.createdAt
        });
      });
      
      // Store all sessions for total weekly time calculation
      setAllWeeklySessions(sessions);
      
      // Load ALL completed sessions for streak calculation (not just current week)
      const allSessionsQuery = query(
        collection(db, 'practiceSessions'),
        where('studentId', '==', targetUser.uid),
        where('completed', '==', true),
        orderBy('endAt', 'desc')
      );
      
      const allSessionsSnapshot = await getDocs(allSessionsQuery);
      const allSessionsData: any[] = [];
      
      allSessionsSnapshot.forEach((doc) => {
        const data = doc.data();
        allSessionsData.push({
          id: doc.id,
          studentId: data.studentId,
          lessonId: data.lessonId,
          plannedTotalMinutes: data.plannedTotalMinutes,
          plannedDurations: data.plannedDurations,
          actualDurations: data.actualDurations,
          completed: data.completed,
          startAt: data.startAt,
          endAt: data.endAt,
          durationSec: data.durationSec,
          taskCounts: data.taskCounts,
          createdAt: data.createdAt
        });
      });
      
      setAllSessions(allSessionsData);
      
      // Calculate goal-reached days based on cumulative daily practice time
      // Use consistent goal calculation: always use the user's practiceSessionDuration
      const goalMinutes = targetUser?.practiceSessionDuration || 30;
      
      // Group sessions by day
      const sessionsByDay: { [dateKey: string]: any[] } = {};
      sessions.forEach(session => {
        const sessionDate = session.endAt?.toDate?.() || new Date(session.endAt || 0);
        const dateKey = sessionDate.toDateString(); // e.g., "Mon Oct 21 2024"
        
        if (!sessionsByDay[dateKey]) {
          sessionsByDay[dateKey] = [];
        }
        sessionsByDay[dateKey].push(session);
      });
      
      // Check each day to see if cumulative practice meets the daily goal
      const goalReachedDays: any[] = [];
      Object.entries(sessionsByDay).forEach(([dateKey, daySessions]) => {
        const dayTotalMinutes = daySessions.reduce((total, session) => {
          return total + (session.durationSec || 0) / 60;
        }, 0);
        
        // If daily total meets goal, add only ONE representative session per day
        // This ensures each calendar day counts as only one weekly goal achievement
        if (dayTotalMinutes >= goalMinutes) {
          // Use the first session of the day as the representative
          goalReachedDays.push(daySessions[0]);
        }
      });
      
      setWeeklyCompletedSessions(goalReachedDays);
      setWeeklyProgressLoaded(true);
    } catch (error) {
      console.error('Error loading weekly progress:', error);
      setWeeklyProgressLoaded(true); // Still mark as loaded to avoid infinite loading
    }
  };

  // Load streak from centralized service
  const loadStreak = async () => {
    const targetUser = isTeacherMode ? viewingStudent : currentUser;
    if (!targetUser?.uid) return;
    
    try {
      setStreakLoading(true);
      const userStatsService = UserStatsService.getInstance();
      const streak = await userStatsService.getUserStreak(targetUser.uid);
      setCurrentStreak(streak);
    } catch (error) {
      console.error('Error loading streak:', error);
      setCurrentStreak(0);
    } finally {
      setStreakLoading(false);
    }
  };

  // Load weekly progress when component mounts or when target user changes
  useEffect(() => {
    const targetUser = isTeacherMode ? viewingStudent : currentUser;
    if (targetUser?.uid) {
      loadWeeklyProgress();
      loadStreak();
    }
  }, [currentUser?.uid, viewingStudent?.uid, isTeacherMode]);

  // Calculate how many badges were earned this week
  const calculateWeeklyBadges = (allBadges: string[], currentWeekSessions: any[], targetUser: any) => {
    // TODO: Badge system needs timestamps to accurately calculate weekly badges
    // For now, return 0 to avoid showing incorrect badge counts
    // The badge system currently only stores badge IDs without timestamps,
    // so we cannot determine when badges were actually earned
    
    // Future implementation should:
    // 1. Add timestamp field to badge data structure
    // 2. Filter allBadges by this week's date range
    // 3. Return actual count of badges earned this week
    
    return 0;
  };

  // Calculate user's weekly points (for current week only)
  const calculateWeeklyPoints = () => {
    let weeklyPoints = 0;
    const pointBreakdown: Array<{points: number, description: string, date?: string}> = [];
    
    // Use allWeeklySessions for current week data
    const currentWeekSessions = allWeeklySessions;
    
    // Calculate daily goal points for current week using Today's Session Goal
    const dailyGoals = new Map<string, number>();
    currentWeekSessions.forEach(session => {
      const sessionDate = new Date(session.endAt?.toDate?.() || session.startAt?.toDate?.() || 0);
      const dateKey = sessionDate.toISOString().split('T')[0];
      const durationMinutes = (session.durationSec || 0) / 60;
      
      // Only count sessions over 10 minutes for daily goal calculation
      if (durationMinutes >= 10) {
        const currentDaily = dailyGoals.get(dateKey) || 0;
        dailyGoals.set(dateKey, currentDaily + durationMinutes);
      }
    });
    
    // Use historical goal calculation: use the goal that was active when each session was completed
    // Group sessions by date and use the goal from the first session of each day
    const dailyGoalData = new Map<string, { goalMinutes: number; totalMinutes: number }>();
    
    currentWeekSessions.forEach(session => {
      const sessionDate = new Date(session.endAt?.toDate?.() || session.startAt?.toDate?.() || 0);
      const dateKey = sessionDate.toISOString().split('T')[0];
      const durationMinutes = (session.durationSec || 0) / 60;
      
      // Only count sessions over 10 minutes for daily goal calculation
      if (durationMinutes >= 10) {
        const existing = dailyGoalData.get(dateKey) || { goalMinutes: 0, totalMinutes: 0 };
        existing.totalMinutes += durationMinutes;
        
        // Use the daily goal that was active when this session was completed
        const sessionDailyGoal = session.dailyGoalAtCompletion || targetUser?.practiceSessionDuration || 30;
        if (existing.goalMinutes === 0) {
          existing.goalMinutes = sessionDailyGoal;
        } else if (existing.goalMinutes !== sessionDailyGoal) {
          console.warn(`Inconsistent daily goals for date ${dateKey}: ${existing.goalMinutes} vs ${sessionDailyGoal}`);
        }
        
        dailyGoalData.set(dateKey, existing);
      }
    });
    
    dailyGoalData.forEach((goalData, date) => {
      const sessionDate = new Date(date);
      const dayName = sessionDate.toLocaleDateString('en-US', { weekday: 'short' });
      const goalMinutes = goalData.goalMinutes;
      const totalMinutes = goalData.totalMinutes;
      
      // New points system: 5 points for < goal, 10 points for ≥ goal
      if (totalMinutes >= goalMinutes) {
        weeklyPoints += 10; // Full goal
        pointBreakdown.push({
          points: 10,
          description: `Goal met (${Math.round(totalMinutes)} min)`,
          date: dayName
        });
      } else if (totalMinutes >= 10) { // Minimum 10 minutes for any points
        weeklyPoints += 5; // Partial goal
        pointBreakdown.push({
          points: 5,
          description: `Practice session (${Math.round(totalMinutes)} min)`,
          date: dayName
        });
      }
    });
    
    // Session type points for current week (only once per day, minimum 10 minutes)
    const sessionPointsByDay = new Map<string, { points: number, description: string, sessionType: string }>();
    
    currentWeekSessions.forEach(session => {
      const sessionDate = new Date(session.endAt?.toDate?.() || session.startAt?.toDate?.() || 0);
      const dayName = sessionDate.toLocaleDateString('en-US', { weekday: 'short' });
      const durationMinutes = (session.durationSec || 0) / 60;
      
      // Only award points for sessions over 10 minutes
      if (durationMinutes >= 10) {
        // Default to free_practice if sessionType is undefined
        const sessionType = session.sessionType || 'free_practice';
        
        // Only award points once per day (take the highest point value if multiple sessions)
        const dayKey = dayName;
        const currentPoints = sessionPointsByDay.get(dayKey);
        const newPoints = sessionType === 'free_practice' ? 3 : 5;
        const newDescription = sessionType === 'free_practice' ? 'Free practice session' : 'Guided practice session';
        
        if (!currentPoints || newPoints > currentPoints.points) {
          sessionPointsByDay.set(dayKey, {
            points: newPoints,
            description: newDescription,
            sessionType: sessionType
          });
        }
      }
    });
    
    // Add session points to weekly total
    sessionPointsByDay.forEach((sessionData, dayName) => {
      weeklyPoints += sessionData.points;
      pointBreakdown.push({
        points: sessionData.points,
        description: sessionData.description,
        date: dayName
      });
    });
    
    // Weekly frequency goals removed - users only get points for individual practice sessions
    // (once per day, 10+ minutes), not for meeting weekly frequency targets
    
    // Multi-category practice points for current week
    const categoryPractice = new Set<string>();
    
    currentWeekSessions.forEach(session => {
      if (session.categoryTimers) {
        Object.entries(session.categoryTimers).forEach(([category, timer]: [string, any]) => {
          if (timer.elapsed > 0) {
            categoryPractice.add(category);
          }
        });
      }
    });
    
    if (categoryPractice.size >= 3) {
      weeklyPoints += 5;
      pointBreakdown.push({
        points: 5,
        description: `Multi-category practice (${categoryPractice.size} categories)`
      });
    }
    
    // Badge points (only for badges earned this week)
    // Calculate which badges were earned this week based on practice sessions
    const weeklyBadgeCount = calculateWeeklyBadges(earnedBadges, currentWeekSessions, targetUser);
    if (weeklyBadgeCount > 0) {
      const badgePoints = weeklyBadgeCount * 5;
      weeklyPoints += badgePoints;
      pointBreakdown.push({
        points: badgePoints,
        description: `${weeklyBadgeCount} badge${weeklyBadgeCount !== 1 ? 's' : ''} earned this week`
      });
    }
    
    // Streak multiplier
    let multiplier = 1;
    let multiplierText = '';
    if (currentStreak >= 100) {
      multiplier = 2.0;
      multiplierText = '2.0x';
    } else if (currentStreak >= 30) {
      multiplier = 1.5;
      multiplierText = '1.5x';
    } else if (currentStreak >= 7) {
      multiplier = 1.2;
      multiplierText = '1.2x';
    }
    
    const basePoints = weeklyPoints;
    weeklyPoints = Math.round(weeklyPoints * multiplier);
    
    // Apply multiplier to all point breakdown entries
    pointBreakdown.forEach(entry => {
      entry.points = Math.round(entry.points * multiplier);
    });
    
    // Add multiplier info if applicable
    if (multiplier > 1) {
      pointBreakdown.unshift({
        points: weeklyPoints - basePoints,
        description: `${multiplierText} streak multiplier (${currentStreak} days)`
      });
    }
    
    return {
      totalPoints: weeklyPoints,
      breakdown: pointBreakdown.slice(-5) // Get last 5 point assignments
    };
  };


  // Get the last 3 badges earned
  const getLastThreeBadges = () => {
    return earnedBadges.slice(-3).reverse(); // Get last 3 and reverse to show most recent first
  };
  // Streak is now loaded from the centralized service
  const targetUser = isTeacherMode ? viewingStudent : currentUser;

  // Use existing session data from props instead of loading separately

  const weeklyPointsData = calculateWeeklyPoints();
  const weeklyPoints = weeklyPointsData.totalPoints;

  // Calculate today's practice time and streak status
  const getTodayPracticeTime = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const todaySessions = weeklyCompletedSessions.filter(session => {
      const sessionDate = new Date(session.endAt?.toDate?.() || session.startAt?.toDate?.() || 0);
      sessionDate.setHours(0, 0, 0, 0);
      return sessionDate.getTime() === today.getTime();
    });
    
    return todaySessions.reduce((total, session) => total + (session.durationSec || 0), 0);
  };

  const getTodayStreakSessions = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return weeklyCompletedSessions.filter(session => {
      const sessionDate = new Date(session.endAt?.toDate?.() || session.startAt?.toDate?.() || 0);
      sessionDate.setHours(0, 0, 0, 0);
      return sessionDate.getTime() === today.getTime() && (session.durationSec || 0) >= 600; // 10+ minutes
    });
  };

  const todayPracticeSeconds = getTodayPracticeTime();
  const todayPracticeMinutes = Math.floor(todayPracticeSeconds / 60);
  const todayStreakSessions = getTodayStreakSessions();
  const hasMetStreakGoal = todayStreakSessions.length > 0;

  // Get current week's days (Sunday to Saturday)
  const getCurrentWeekDays = () => {
    const today = new Date();
    const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - currentDay); // Go back to Sunday
    startOfWeek.setHours(0, 0, 0, 0);
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push(day);
    }
    return days;
  };

  // Check if a specific day met the daily goal (based on user's practiceSessionDuration)
  const didDayMeetGoal = (day: Date) => {
    const dayStart = new Date(day);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart);
    dayEnd.setHours(23, 59, 59, 999);
    
    const daySessions = allWeeklySessions.filter(session => {
      const sessionDate = session.endAt?.toDate?.() || new Date(session.endAt || 0);
      return sessionDate >= dayStart && sessionDate <= dayEnd;
    });
    
    // Calculate total practice time for the day
    const totalMinutes = daySessions.reduce((total, session) => {
      return total + (session.durationSec || 0) / 60;
    }, 0);
    
    // Check if total practice time meets the daily goal
    const goalMinutes = targetUser?.practiceSessionDuration || 30;
    return totalMinutes >= goalMinutes;
  };

  // Get daily practice goal completion for the current week
  const getWeeklyDailyGoalStatus = () => {
    const weekDays = getCurrentWeekDays();
    return weekDays.map(day => ({
      day,
      dayOfWeek: day.getDay(), // 0 = Sunday, 1 = Monday, etc.
      metGoal: didDayMeetGoal(day),
      isToday: day.getTime() === new Date().setHours(0, 0, 0, 0)
    }));
  };

  const dayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const weeklyDailyGoalStatus = getWeeklyDailyGoalStatus();

  // Determine what to display in the timer
  const getTimerDisplay = () => {
    if (plannedTotalMinutes > 0) {
      return formatTime(overallRemainingTime);
    } else if (targetUser?.practiceSessionDuration) {
      return `${targetUser.practiceSessionDuration}:00`;
    }
    return '00:00';
  };

  // Determine if we should show the progress bar
  const shouldShowProgressBar = () => {
    if (plannedTotalMinutes > 0) {
      return true; // Show progress bar when we have planned tasks
    }
    if (isTeacherMode) {
      return targetUser?.practiceSessionDuration;
    }
    return currentUser?.practiceSessionDuration;
  };

  // Determine the progress bar width
  const getProgressBarWidth = () => {
    if (isPracticeSessionActive) {
      return `${currentSessionProgress}%`;
    }
    if (plannedTotalMinutes > 0) {
      // Calculate progress based on planned vs remaining time
      const targetTotalMinutes = targetUser?.practiceSessionDuration || plannedTotalMinutes;
      
      // During planning phase, overallRemainingTime represents allocated time
      // During practice phase, we need to calculate allocated time differently
      let allocatedMinutes;
      if (isPracticeSessionActive) {
        // During practice: allocated = target - remaining
        allocatedMinutes = targetTotalMinutes - (overallRemainingTime / 60);
      } else {
        // During planning: overallRemainingTime IS the allocated time
        allocatedMinutes = overallRemainingTime / 60;
      }
      
      const progress = Math.min(100, (allocatedMinutes / targetTotalMinutes) * 100);
      return `${Math.max(0, progress)}%`;
    }
    return '0%';
  };

  // Determine the progress bar title
  const getProgressBarTitle = () => {
    if (isPracticeSessionActive) {
      return `Practice session in progress: ${currentSessionProgress.toFixed(1)}% complete`;
    }
    if (plannedTotalMinutes > 0) {
      const targetTotalMinutes = targetUser?.practiceSessionDuration || plannedTotalMinutes;
      
      // During planning phase, overallRemainingTime represents allocated time
      let allocatedMinutes;
      if (isPracticeSessionActive) {
        // During practice: allocated = target - remaining
        allocatedMinutes = targetTotalMinutes - (overallRemainingTime / 60);
      } else {
        // During planning: overallRemainingTime IS the allocated time
        allocatedMinutes = overallRemainingTime / 60;
      }
      
      return `Planning Progress: ${Math.round(allocatedMinutes)} of ${targetTotalMinutes} minutes allocated`;
    }
    return 'Start a practice session to see progress here';
  };

  return (
    <div className="bg-gradient-to-br from-indigo-600 to-purple-700 shadow-xl rounded-lg p-2 sm:p-3 md:p-4 text-white">
      {/* Top Row - Four sections: Streak, Timer, Points, Badges */}
      <div className="flex justify-between items-start gap-1 sm:gap-2">
        {/* Left - Streak Tracking Icon */}
        <div 
          className="relative bg-white bg-opacity-20 backdrop-blur-sm rounded-full p-1 sm:p-2 hover:bg-opacity-30 transition-all duration-200 cursor-pointer group"
          onMouseEnter={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setStreakTooltipPosition({
              x: rect.right + 8, // Position to the right
              y: rect.bottom + 8   // Position below
            });
            setShowStreakTooltip(true);
          }}
          onMouseLeave={() => setShowStreakTooltip(false)}
        >
          <div className="text-sm sm:text-xl md:text-2xl">🔥</div>
          <div className="text-xs font-bold opacity-90 group-hover:opacity-100">
            Day{currentStreak !== 1 ? 's' : ''}
          </div>
          <div className="text-xs sm:text-sm md:text-base font-bold mt-0.5 sm:mt-1 opacity-90 group-hover:opacity-100">
            {currentStreak}
          </div>
          
          {/* Streak Status Tooltip - Portal */}
          {showStreakTooltip && createPortal(
            <div 
              className="fixed w-48 max-w-[90vw] bg-gray-900 text-white rounded-lg shadow-xl p-3 text-center"
              style={{
                left: Math.min(streakTooltipPosition.x, window.innerWidth - 200),
                top: Math.min(streakTooltipPosition.y, window.innerHeight - 150),
                zIndex: 99999
              }}
            >
              {hasMetStreakGoal ? (
                <div>
                  <div className="text-green-400 text-xl mb-1">🎉</div>
                  <div className="text-xs font-semibold text-green-400 mb-1">Streak Goal Met!</div>
                  <div className="text-xs text-gray-300">
                    {todayStreakSessions.length} session{todayStreakSessions.length !== 1 ? 's' : ''} completed today
                  </div>
                </div>
              ) : (
                <div>
                  <div className="text-orange-400 text-xl mb-1">⏰</div>
                  <div className="text-xs font-semibold text-orange-400 mb-1">Keep Your Streak!</div>
                  <div className="text-xs text-gray-300">
                    Complete a 10+ minute session
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {todayPracticeMinutes} min today
                  </div>
                </div>
              )}
            </div>,
            document.body
          )}
        </div>
        
        {/* Center Left - Title and Timer */}
        <div className="flex-1 text-center mx-1 sm:mx-2">
          <h3 className="text-xs sm:text-lg md:text-xl font-bold mb-1">
            Daily Practice Goal
            {isPracticeSessionActive && (
              <span className="ml-1 sm:ml-2 text-xs bg-green-500 text-white px-1 sm:px-2 py-0.5 sm:py-1 rounded-full animate-pulse">
                🔴 LIVE
              </span>
            )}
            {showResetButton && onResetTimers && (
              <button
                onClick={onResetTimers}
                className="ml-1 sm:ml-2 px-1 sm:px-2 py-0.5 sm:py-1 bg-white bg-opacity-20 hover:bg-opacity-30 text-white text-xs rounded-md transition-all duration-200"
                title="Reset all task timers to 0 and restore default daily goal"
              >
                Reset Timers
              </button>
            )}
          </h3>
          
          {/* Timer - Centered */}
          <div className="text-lg sm:text-3xl md:text-5xl font-mono font-bold">
            {getTimerDisplay()}
          </div>
        </div>
        
            {/* Center Right - Points Icon */}
            <div 
              className="relative bg-white bg-opacity-20 backdrop-blur-sm rounded-full p-1 sm:p-2 hover:bg-opacity-30 transition-all duration-200 cursor-pointer group"
              onMouseEnter={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setPointsTooltipPosition({
                  x: rect.left - 320, // Position to the left
                  y: rect.bottom + 8   // Position below
                });
                setShowPointsTooltip(true);
              }}
              onMouseLeave={() => setShowPointsTooltip(false)}
            >
              <div className="text-sm sm:text-xl md:text-2xl">⭐</div>
              <div className="text-[10px] font-bold text-white group-hover:opacity-100">
                This Week
              </div>
              <div className="text-xs font-bold mt-0.5 sm:mt-1 text-white group-hover:opacity-100">
                {weeklyPoints}
              </div>
          
          {/* Points Tooltip - Portal */}
          {showPointsTooltip && createPortal(
            <div 
              className="fixed w-64 max-w-[90vw] bg-gray-900 text-white rounded-lg shadow-xl p-3"
              style={{
                left: Math.max(8, Math.min(pointsTooltipPosition.x, window.innerWidth - 264)),
                top: Math.min(pointsTooltipPosition.y, window.innerHeight - 200),
                zIndex: 99999
              }}
            >
              <div className="text-center mb-2">
                <div className="text-yellow-400 text-xl mb-1">⭐</div>
                <div className="text-xs font-semibold text-yellow-400 mb-1">Weekly Points</div>
                <div className="text-xs text-gray-300">
                  {weeklyPoints} points this week
                </div>
              </div>
              
              {weeklyPointsData.breakdown.length > 0 && (
                <div className="border-t border-gray-700 pt-2">
                  <div className="text-xs font-semibold text-gray-300 mb-1">Recent Sources:</div>
                  <div className="space-y-1 max-h-20 overflow-y-auto">
                    {weeklyPointsData.breakdown.slice().reverse().slice(0, 3).map((entry, index) => (
                      <div key={index} className="flex justify-between items-center text-xs">
                        <div className="flex-1 text-left truncate">
                          <div className="text-gray-300 truncate">{entry.description}</div>
                        </div>
                        <div className="text-yellow-400 font-semibold ml-1">
                          +{entry.points}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {weeklyPointsData.breakdown.length === 0 && (
                <div className="text-xs text-gray-400 text-center">
                  Start practicing to earn points!
                </div>
              )}
            </div>,
            document.body
          )}
        </div>
        
        {/* Right - Badges Earned Icon */}
        <div 
          className="relative bg-white bg-opacity-20 backdrop-blur-sm rounded-full p-1 sm:p-2 hover:bg-opacity-30 transition-all duration-200 cursor-pointer group"
          onMouseEnter={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setBadgeTooltipPosition({
              x: rect.left - 320, // Position to the left
              y: rect.bottom + 8   // Position below
            });
            setShowTooltip(true);
          }}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <div className="text-sm sm:text-xl md:text-2xl">🏅</div>
          <div className="text-[10px] font-bold text-white group-hover:opacity-100">
            Badge{earnedBadges.length !== 1 ? 's' : ''}
          </div>
          <div className="text-xs font-bold mt-0.5 sm:mt-1 text-white group-hover:opacity-100">
            {badgesLoaded ? earnedBadges.length : '0'}
          </div>
          
          {/* Badge Tooltip - Portal */}
          {showTooltip && earnedBadges.length > 0 && createPortal(
            <div 
              className="fixed w-64 max-w-[90vw] bg-gray-900 text-white rounded-lg shadow-xl p-3"
              style={{
                left: Math.max(8, Math.min(badgeTooltipPosition.x, window.innerWidth - 264)),
                top: Math.min(badgeTooltipPosition.y, window.innerHeight - 200),
                zIndex: 99999
              }}
            >
              <div className="text-xs font-semibold mb-2 text-center">Recent Badges</div>
              <div className="space-y-1">
                {getLastThreeBadges().map((badgeId, index) => {
                  const badge = badgeDefinitions[badgeId];
                  if (!badge) return null;
                  
                  return (
                    <div key={badgeId} className="flex items-center space-x-2 p-1.5 bg-gray-800 rounded">
                      <div className="text-lg">{badge.emoji}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-xs truncate">{badge.name}</div>
                        <div className="text-xs text-gray-300 truncate">{badge.description}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {earnedBadges.length > 3 && (
                <div className="text-xs text-gray-400 text-center mt-2">
                  +{earnedBadges.length - 3} more badges
                </div>
              )}
            </div>,
            document.body
          )}
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="mt-3">
        {shouldShowProgressBar() && (
          <div 
            className="w-full bg-white bg-opacity-20 rounded-full h-2 sm:h-3 overflow-hidden cursor-help"
            title={getProgressBarTitle()}
          >
            <div 
              className="bg-white h-2 sm:h-3 rounded-full transition-all duration-1000"
              style={{ 
                width: getProgressBarWidth(),
                transform: 'translateX(0)' // Ensure it fills from left to right
              }}
            ></div>
          </div>
        )}
      </div>

      {/* Weekly Daily Practice Goal Tracker */}
      {targetUser?.practiceSessionDuration && weeklyProgressLoaded && (
        <div className="mt-4 pt-3 border-t border-white border-opacity-20">
          <div className="text-xs sm:text-sm font-medium mb-3 flex items-center justify-center">
            Daily Practice Goal: {targetUser.practiceSessionDuration} minutes <span className="text-xs text-white/50">(Resets every Sunday)</span>
          </div>
          <div className="flex items-center justify-between mb-3">
            {/* Daily Practice Total - Left */}
            <div className="text-center">
              <div className="text-[10px] text-white mb-1">Today's Practice</div>
              <div className="text-xs font-bold text-white">
                {(() => {
                  const today = new Date();
                  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                  const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);
                  
                  // Calculate today's practice time from ALL completed sessions (regardless of goal)
                  const todaySessions = allWeeklySessions.filter(session => {
                    const sessionDate = session.endAt?.toDate?.() || new Date(session.endAt || 0);
                    return sessionDate >= todayStart && sessionDate < todayEnd;
                  });
                  
                  const todayMinutes = todaySessions.reduce((total, session) => {
                    return total + (session.durationSec || 0) / 60;
                  }, 0);
                  
                  return `${Math.round(todayMinutes)}m`;
                })()}
              </div>
            </div>
            
            {/* Weekly Daily Goal Tracker Circles - Center */}
            <div className="flex items-center justify-center space-x-1 sm:space-x-2">
              {weeklyDailyGoalStatus.map((dayStatus, index) => {
                const isCompleted = dayStatus.metGoal;
                const isToday = dayStatus.isToday;
                const dayLabel = dayLabels[dayStatus.dayOfWeek];
                
                // Calculate today's progress for tooltip
                const today = new Date();
                const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);
                
                const todaySessions = allWeeklySessions.filter(session => {
                  const sessionDate = session.endAt?.toDate?.() || new Date(session.endAt || 0);
                  return sessionDate >= todayStart && sessionDate < todayEnd;
                });
                
                const todayMinutes = todaySessions.reduce((total, session) => {
                  return total + (session.durationSec || 0) / 60;
                }, 0);
                
                const goalMinutes = targetUser?.practiceSessionDuration || 30;
                const remainingMinutes = Math.max(0, goalMinutes - todayMinutes);
                
                // Tooltip text
                const tooltipText = isToday && !isCompleted && remainingMinutes > 0 
                  ? `${remainingMinutes.toFixed(0)} more minutes needed today!`
                  : isToday && !isCompleted && remainingMinutes <= 0
                  ? "Goal reached today! 🎉"
                  : isCompleted
                  ? `Goal met on ${dayStatus.day.toLocaleDateString('en-US', { weekday: 'long' })}! 🎉`
                  : `${dayStatus.day.toLocaleDateString('en-US', { weekday: 'long' })} - Goal not met yet`;
                
                // Different colors for each day
                const colors = [
                  'from-pink-500 to-rose-500', // Sunday - Pink
                  'from-orange-500 to-amber-500', // Monday - Orange
                  'from-yellow-500 to-lime-500', // Tuesday - Yellow
                  'from-green-500 to-emerald-500', // Wednesday - Green
                  'from-blue-500 to-cyan-500', // Thursday - Blue
                  'from-purple-500 to-violet-500', // Friday - Purple
                  'from-indigo-500 to-blue-500' // Saturday - Indigo
                ];
                
                const colorClass = colors[dayStatus.dayOfWeek];
                
                return (
                  <div
                    key={index}
                    className={`relative w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full border-2 sm:border-3 flex items-center justify-center transition-all duration-500 transform hover:scale-110 ${
                      isCompleted
                        ? `bg-gradient-to-br ${colorClass} text-white border-white shadow-lg ${
                            isToday ? 'animate-pulse animate-bounce' : ''
                          }`
                        : isToday
                        ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-gray-600 border-gray-300 animate-pulse'
                        : 'bg-transparent border-white border-opacity-30 text-white'
                    }`}
                    title={tooltipText}
                  >
                    {isCompleted ? (
                      <div className="relative">
                        {/* Checkmark with sparkle effect */}
                        <svg 
                          className={`w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 ${isToday ? 'animate-spin' : ''}`} 
                          fill="currentColor" 
                          viewBox="0 0 20 20"
                        >
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {/* Sparkle effect for completed days */}
                        {isToday && (
                          <>
                            <div className="absolute -top-1 -right-1 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-yellow-300 rounded-full animate-ping"></div>
                            <div className="absolute -bottom-1 -left-1 w-1 h-1 sm:w-1.5 sm:h-1.5 bg-yellow-400 rounded-full animate-ping delay-150"></div>
                            <div className="absolute -top-1 -left-1 w-0.5 h-0.5 sm:w-1 sm:h-1 bg-yellow-200 rounded-full animate-ping delay-300"></div>
                          </>
                        )}
                      </div>
                    ) : isToday && !isCompleted ? (
                      <div className="text-xs font-bold">?</div>
                    ) : (
                      <div className="text-xs font-bold">
                        {dayLabel}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Weekly Practice Total - Right */}
            <div className="text-center">
              <div className="text-[10px] text-white mb-1">This Week</div>
              <div className="text-xs font-bold text-white">
                {(() => {
                  const weeklyMinutes = allWeeklySessions.reduce((total, session) => {
                    return total + (session.durationSec || 0) / 60;
                  }, 0);
                  return `${Math.round(weeklyMinutes)}m`;
                })()}
              </div>
            </div>
          </div>

          {/* Progress message */}
          <div className="text-center">
            <p className="text-sm font-medium">
              {(() => {
                const completedDays = weeklyDailyGoalStatus.filter(day => day.metGoal).length;
                const totalDays = weeklyDailyGoalStatus.length;
                
                if (completedDays === 0) {
                  return <span className="text-white">Start your first daily practice goal this week!</span>;
                } else if (completedDays < totalDays) {
                  return <span className="text-white">{completedDays} of {totalDays} days met daily goal this week</span>;
                } else if (completedDays === totalDays) {
                  return <span className="text-yellow-200 font-bold">🎉 Perfect week! You've met your daily goal every day! 🎉</span>;
                } else {
                  return <span className="text-yellow-200 font-bold">🚀 Amazing! You've exceeded your weekly goal! 🚀</span>;
                }
              })()}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyPracticeGoalCard;
